/**
 * Expanded prompt architecture for one style row.
 *
 * Same layout as the LifeScribe original (labelled monospace cards with a copy
 * button and a character count) with two changes: it reads the real snake_case
 * columns instead of a lossy camelCase mapping, and it reports copy results
 * through a callback instead of dispatching into the admin's Redux store — Quill
 * pages own their own snackbar.
 */

import React from 'react';
import { Box, Typography, Card, CardContent, IconButton, Tooltip, Alert } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { colors, typography } from '../../../theme/designTokens';
import { StyleFamily, StyleRow } from './types';

export interface PromptExpandSectionProps {
  row: StyleRow;
  family: StyleFamily;
  onCopy: (text: string, label: string) => void;
}

const PromptExpandSection: React.FC<PromptExpandSectionProps> = ({ row, family, onCopy }) => {
  const sections: Array<{ title: string; content: string }> = [];

  if (row.style_dna) sections.push({ title: 'Style DNA', content: row.style_dna });
  if (family === 'writing' && row.transformation_formula) {
    sections.push({ title: 'Transformation Formula', content: row.transformation_formula });
  }
  if (family === 'illustration' && row.visual_approach) {
    sections.push({ title: 'Visual Approach', content: row.visual_approach });
  }
  if (family === 'illustration' && row.prompt_construction) {
    sections.push({ title: 'Prompt Construction Formula', content: row.prompt_construction });
  }
  if (row.truth_integration) {
    sections.push({ title: 'Truth Integration', content: row.truth_integration });
  }

  if (sections.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No prompt data available for this style.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 1.5, fontWeight: 600, color: colors.deepTeal[500], fontSize: '0.78rem' }}
      >
        PROMPT ARCHITECTURE
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {sections.map((section) => (
          <Card key={section.title} variant="outlined" sx={{ bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                  {section.title}
                </Typography>
                <Tooltip title={`Copy ${section.title}`}>
                  <IconButton
                    size="small"
                    aria-label={`Copy ${section.title}`}
                    onClick={() => onCopy(section.content, section.title)}
                  >
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: typography.fonts.mono,
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.8rem',
                  color: '#475569',
                }}
              >
                {section.content}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {section.content.length} characters
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default PromptExpandSection;
